import { reactive, computed, getCurrentInstance } from '@vue/composition-api';
import Vue from 'vue';
import {
  getTranslations,
  getLocales,
  updateTranslationsForKey,
  exportTranslations as apiExportTranslations,
} from '@/http/translations';
import {
  translationCategories,
  flattenTranslations,
  unflattenTranslations,
  groupTranslationsByCategory,
  getCategoryForKey,
  calculateMissingTranslationsStats,
  filterIncompleteTranslations,
} from '@/utils/translations';
import { getLanguages } from '@/constants/languages';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export const useTranslations = () => {
  const { proxy } = getCurrentInstance();

  // Reactive state
  const state = reactive({
    activeCategory: 'general',
    allLocaleTranslations: {}, // { 'pl-PL': {}, 'en-US': {}, ... }
    allLocaleFlattenedTranslations: {}, // { 'pl-PL': {}, 'en-US': {}, ... }
    categorizedTranslations: {}, // Grouped by category
    translationCounts: {},
    availableLocales: [],
    availableLanguages: getLanguages(),
    globalSearch: '',
    globalSearchResults: [],
    loading: false,
    loadingLocales: [], // Track which locales are currently loading
    // Statistics
    recentlyModified: 0,
    missingTranslations: 0,
    translationStats: null, // Missing translation statistics
  });

  // Computed properties
  const totalTranslations = computed(() => {
    // Count unique keys across all locales
    const allKeys = new Set();
    Object.values(state.allLocaleFlattenedTranslations).forEach((flat) => {
      Object.keys(flat).forEach((key) => allKeys.add(key));
    });
    return allKeys.size;
  });

  const missingTranslationsCount = computed(() => {
    // Always return the total missing translations count, regardless of filters
    // The filter should only affect what's displayed, not what's counted in the header
    return state.missingTranslations;
  });

  const localeOptions = computed(() => {
    return state.availableLocales.map((locale) => ({
      value: locale.code,
      text: locale.name,
    }));
  });

  // Notification methods
  const showSuccessNotification = (title, text) => {
    proxy.$toast({
      component: ToastificationContent,
      props: {
        title,
        icon: 'CheckIcon',
        variant: 'success',
        text,
      },
    });
  };

  const showErrorNotification = (title, text) => {
    proxy.$toast({
      component: ToastificationContent,
      props: {
        title,
        icon: 'AlertTriangleIcon',
        variant: 'danger',
        text,
      },
    });
  };

  // Core methods
  const loadLocales = async () => {
    try {
      const response = await getLocales();
      state.availableLocales = response.data;
    } catch (error) {
      console.error('Error loading locales:', error);
      state.availableLocales = [{ code: 'pl-PL', name: 'Polski (Polska)' }];
    }
  };

  // Load translations for all available locales
  const loadAllTranslations = async () => {
    state.loading = true;
    try {
      const localePromises = state.availableLanguages.map(async (lang) => {
        const locale = lang.locale;
        state.loadingLocales.push(locale);
        try {
          const response = await getTranslations(locale);
          state.allLocaleTranslations[locale] = response.data;
          state.allLocaleFlattenedTranslations[locale] = flattenTranslations(response.data);
        } catch (error) {
          console.error(`Error loading translations for ${locale}:`, error);
          state.allLocaleTranslations[locale] = {};
          state.allLocaleFlattenedTranslations[locale] = {};
        } finally {
          const index = state.loadingLocales.indexOf(locale);
          if (index > -1) {
            state.loadingLocales.splice(index, 1);
          }
        }
      });
      await Promise.all(localePromises);
      processTranslations();
      showSuccessNotification('Sukces', 'Tłumaczenia zostały załadowane dla wszystkich języków');
    } catch (error) {
      console.error('Error loading all translations:', error);
      showErrorNotification('Błąd', 'Nie udało się załadować tłumaczeń');
    } finally {
      state.loading = false;
    }
  };

  const processTranslations = () => {
    // For simplicity, use the first language's locale as the base for categories
    const firstLocale = state.availableLanguages[0]?.locale;
    if (!firstLocale) return;

    const baseFlat = state.allLocaleFlattenedTranslations[firstLocale] || {};
    state.categorizedTranslations = groupTranslationsByCategory(baseFlat);

    // Calculate missing translation statistics
    state.translationStats = calculateMissingTranslationsStats(
      state.allLocaleFlattenedTranslations,
      state.availableLanguages,
    );

    // Calculate counts (including missing counts)
    state.translationCounts = {};
    Object.keys(state.categorizedTranslations).forEach((categoryId) => {
      const categoryStats = state.translationStats.byCategory[categoryId];
      state.translationCounts[categoryId] = {
        total: Object.keys(state.categorizedTranslations[categoryId]).length,
        missing: categoryStats ? categoryStats.missing : 0,
        percentage: categoryStats ? categoryStats.percentage : 100,
      };
    });

    // Calculate overall statistics
    calculateStatistics();
  };

  const calculateStatistics = () => {
    if (state.translationStats) {
      // Use the direct calculation approach that was working well
      let missingCount = 0;
      const nonPolishLanguages = state.availableLanguages.filter((lang) => lang.lang !== 'pl');

      // Get all unique keys from all categories
      Object.keys(translationCategories).forEach((categoryId) => {
        const categoryTranslations = state.categorizedTranslations[categoryId] || {};
        Object.keys(categoryTranslations).forEach((key) => {
          const allTranslationsForKey = getTranslationsForKey(key);
          const polishText = allTranslationsForKey['pl-PL'];

          // Only count keys that have Polish text (source language)
          if (polishText && polishText.trim()) {
            nonPolishLanguages.forEach((lang) => {
              const translation = allTranslationsForKey[lang.locale];
              if (!translation || translation.trim() === '') {
                missingCount++;
              }
            });
          }
        });
      });

      state.missingTranslations = missingCount;
      // Mock recent modifications for now
      state.recentlyModified = Math.floor(Math.random() * 50);
    }
  };

  // Get translations for all locales for a given key
  const getTranslationsForKey = (key) => {
    const result = {};
    state.availableLanguages.forEach((lang) => {
      const locale = lang.locale;
      const translations = state.allLocaleFlattenedTranslations[locale] || {};
      result[locale] = translations[key] || '';
    });
    return result;
  };

  // Get translations for a specific category
  const getTranslationsForCategory = (categoryId) => {
    const categoryTranslations = state.categorizedTranslations[categoryId] || {};

    return categoryTranslations;
  };

  // Get locale code from full locale string (e.g., 'pl-PL' -> 'pl')
  const getLanguageCode = (locale) => {
    return locale.split('-')[0];
  };

  // Update translations for a key in multiple locales
  const handleUpdateMultiLocaleTranslation = async (key, localeValues) => {
    try {
      await updateTranslationsForKey(key, localeValues);

      // Update local state for all locales
      Object.entries(localeValues).forEach(([locale, value]) => {
        // Ensure the locale exists in our state
        if (!state.allLocaleFlattenedTranslations[locale]) {
          Vue.set(state.allLocaleFlattenedTranslations, locale, {});
        }
        if (!state.allLocaleTranslations[locale]) {
          Vue.set(state.allLocaleTranslations, locale, {});
        }

        // Update the flattened translations using Vue.set for reactivity
        Vue.set(state.allLocaleFlattenedTranslations[locale], key, value);

        // Update the nested translations structure with error handling
        try {
          Vue.set(
            state.allLocaleTranslations,
            locale,
            unflattenTranslations(state.allLocaleFlattenedTranslations[locale]),
          );
        } catch (unflattenError) {
          console.error(`Error unflattening translations for locale ${locale}:`, unflattenError);
          // Fallback: just use the flattened structure
          Vue.set(state.allLocaleTranslations, locale, state.allLocaleFlattenedTranslations[locale]);
        }
      });

      // Reprocess translations to update categories and counts
      processTranslations();

      showSuccessNotification('Sukces', 'Tłumaczenia zostały zaktualizowane we wszystkich językach');
    } catch (error) {
      console.error('Error updating multi-locale translation:', error);
      showErrorNotification('Błąd', 'Nie udało się zaktualizować tłumaczeń');
      throw error; // Re-throw to allow components to handle the error
    }
  };

  // Force reactivity update (utility method)
  const forceUpdate = () => {
    processTranslations();
  };

  // Global search, category, and other UI logic remain unchanged
  const handleCategoryChange = (categoryId) => {
    state.activeCategory = categoryId;
    // Clear selected translation key when changing categories
    clearSelectedTranslationKey();
  };

  const handleGlobalSearch = (searchTerm) => {
    state.globalSearch = searchTerm;
    if (!searchTerm) {
      state.globalSearchResults = [];
      return;
    }
    const results = [];
    // Search in all locales, but only show unique keys
    const allKeys = new Set();
    state.availableLanguages.forEach((lang) => {
      const locale = lang.locale;
      const flat = state.allLocaleFlattenedTranslations[locale] || {};
      Object.keys(flat).forEach((key) => allKeys.add(key));
    });
    allKeys.forEach((key) => {
      // Use the first language's locale's value for search
      const value = state.allLocaleFlattenedTranslations[state.availableLanguages[0]?.locale]?.[key] || '';
      if (
        key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase()))
      ) {
        const topLevelKey = key.split('.')[0];
        const category = getCategoryForKey(topLevelKey);
        results.push({ key, value, category });
      }
    });
    state.globalSearchResults = results;
  };

  const clearGlobalSearch = () => {
    state.globalSearch = '';
    state.globalSearchResults = [];
  };

  const navigateToResult = (result) => {
    state.activeCategory = result.category;
    state.selectedTranslationKey = result.key; // Store the selected translation key
    clearGlobalSearch();
  };

  const clearSelectedTranslationKey = () => {
    state.selectedTranslationKey = null;
  };

  // Add missing reactive state properties
  Object.assign(state, {
    selectedLocale: 'pl-PL',
    showImportModal: false,
    showCreateLocaleModal: false,
    importFile: null,
    importOptions: {},
    newLocale: { code: '', name: '' },
    selectedTranslationKey: null, // Track selected translation key from global search
  });

  // Add missing event handlers
  const handleLocaleChange = (locale) => {
    state.selectedLocale = locale;
    // Reload translations for the new locale if needed
  };

  const handleUpdateTranslation = (key, value) => {
    // Update translation for current locale
    const locale = state.selectedLocale;
    const updates = { [locale]: value };
    return handleUpdateMultiLocaleTranslation(key, updates);
  };

  const handleAddTranslation = (key, value) => {
    // Add new translation for current locale
    return handleUpdateTranslation(key, value);
  };

  const exportTranslations = async () => {
    try {
      const response = await apiExportTranslations(state.selectedLocale);
      // Handle export response (e.g., download file)
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `translations_${state.selectedLocale}.json`;
      a.click();
      showSuccessNotification('Sukces', 'Tłumaczenia zostały wyeksportowane');
    } catch (error) {
      console.error('Error exporting translations:', error);
      showErrorNotification('Błąd', 'Nie udało się wyeksportować tłumaczeń');
    }
  };

  const handleImport = async (importData) => {
    try {
      // Handle import logic here
      showSuccessNotification('Sukces', 'Tłumaczenia zostały zaimportowane');
      state.showImportModal = false;
      await loadAllTranslations();
    } catch (error) {
      console.error('Error importing translations:', error);
      showErrorNotification('Błąd', 'Nie udało się zaimportować tłumaczeń');
    }
  };

  const resetImportForm = () => {
    state.importFile = null;
    state.importOptions = {};
  };

  const handleCreateLocale = async (localeData) => {
    try {
      // Handle locale creation logic here
      showSuccessNotification('Sukces', 'Nowy język został utworzony');
      state.showCreateLocaleModal = false;
      await loadLocales();
    } catch (error) {
      console.error('Error creating locale:', error);
      showErrorNotification('Błąd', 'Nie udało się utworzyć nowego języka');
    }
  };

  const resetCreateLocaleForm = () => {
    state.newLocale = { code: '', name: '' };
  };

  const loadTranslations = async () => {
    // Reload translations for current locale
    await loadAllTranslations();
  };

  // Handle translations updated event (e.g., after AI translation acceptance)
  const handleTranslationsUpdated = async () => {
    try {
      // Temporarily disable loading state to prevent UI flicker
      const wasLoading = state.loading;

      // Reload all translations silently (without success notification)
      state.loading = true;
      const localePromises = state.availableLanguages.map(async (lang) => {
        const locale = lang.locale;
        try {
          const response = await getTranslations(locale);
          state.allLocaleTranslations[locale] = response.data;
          state.allLocaleFlattenedTranslations[locale] = flattenTranslations(response.data);
        } catch (error) {
          console.error(`Error loading translations for ${locale}:`, error);
        }
      });

      await Promise.all(localePromises);
      processTranslations();
      state.loading = wasLoading;
    } catch (error) {
      console.error('Error refreshing translations:', error);
      showErrorNotification('Błąd', 'Nie udało się odświeżyć tłumaczeń');
    }
  };

  // Efficiently update specific translations in state without full reload
  const updateTranslationsInState = (updatedTranslations) => {
    try {
      // Update the state with new translations
      Object.entries(updatedTranslations).forEach(([key, localeValues]) => {
        Object.entries(localeValues).forEach(([locale, value]) => {
          // Ensure the locale exists in our state
          if (!state.allLocaleFlattenedTranslations[locale]) {
            Vue.set(state.allLocaleFlattenedTranslations, locale, {});
          }
          if (!state.allLocaleTranslations[locale]) {
            Vue.set(state.allLocaleTranslations, locale, {});
          }

          // Update the flattened translations using Vue.set for reactivity
          Vue.set(state.allLocaleFlattenedTranslations[locale], key, value);

          // Update the nested translations structure with error handling
          try {
            Vue.set(
              state.allLocaleTranslations,
              locale,
              unflattenTranslations(state.allLocaleFlattenedTranslations[locale]),
            );
          } catch (unflattenError) {
            console.error(`Error unflattening translations for locale ${locale}:`, unflattenError);
            // Fallback: just use the flattened structure
            Vue.set(state.allLocaleTranslations, locale, state.allLocaleFlattenedTranslations[locale]);
          }
        });
      });

      // Reprocess translations to update categories and counts
      processTranslations();

      return true;
    } catch (error) {
      console.error('Error updating translations in state:', error);
      return false;
    }
  };

  return {
    // State
    state,
    translationCategories,
    totalTranslations,
    missingTranslationsCount,
    localeOptions,
    // Methods
    loadLocales,
    loadTranslations,
    loadAllTranslations,
    handleLocaleChange,
    handleCategoryChange,
    handleGlobalSearch,
    clearGlobalSearch,
    navigateToResult,
    clearSelectedTranslationKey,
    getTranslationsForKey,
    getTranslationsForCategory,
    getLanguageCode,
    handleUpdateTranslation,
    handleUpdateMultiLocaleTranslation,
    handleAddTranslation,
    exportTranslations,
    handleImport,
    resetImportForm,
    handleCreateLocale,
    resetCreateLocaleForm,
    handleTranslationsUpdated,
    forceUpdate,
    updateTranslationsInState,
  };
};
